import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import ModalFeedDetailHeader from './ModalFeedDetailHeader';
import ModalFeedDetailBody from './ModalFeedDetailBody';
import { Feed } from '../../../scenes/Feed/FeedPage';
import { Col } from '../../../lib/utils';
import ModalFeedDetailFooter from './ModalFeedDetailFooter';

const slideIn = keyframes`
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
`;

const slideOut = keyframes`
    from { transform: translateX(0); }
    to { transform: translateX(100%); }
`;

const DrawerOverlay = styled.div<{ $isClosing: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.35);
    z-index: 60;
    animation: ${({ $isClosing }) =>
        $isClosing
            ? css`fadeOut 0.25s ease-out forwards`
            : css`fadeIn 0.25s ease-out forwards`};

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
    @keyframes fadeOut {
        from { opacity: 1; }
        to { opacity: 0; }
    }
`;

const DrawerPanel = styled.div<{ $isClosing: boolean }>`
    position: fixed;
    top: 0;
    right: 0;
    width: 720px;
    height: 100vh;
    background: #F5F9FF;
    box-shadow: -8px 0 24px rgba(0, 0, 0, 0.15);
    z-index: 61;
    display: flex;
    flex-direction: column;
    animation: ${({ $isClosing }) =>
        $isClosing
            ? css`${slideOut} 0.25s ease-out forwards`
            : css`${slideIn} 0.3s ease-out forwards`};
    overflow-y: auto;

    @media (max-width: 760px) {
        width: min(720px, 100vw);
    }
`;

type Props = {
    modalObject: Feed;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    setIsConditionModal?: Dispatch<SetStateAction<boolean>>;
    onClickImageAtDetailModal?: (arr: any) => void;
    onClickVideoAtDetailModal?: any;
    isIOSiPad?: () => boolean;
    isConditionModal?: boolean;
    isImageModal?: boolean;
    isVideoModal?: boolean;
    // Navigation props
    feedList?: Feed[];
    currentIndex?: number;
    onNavigate?: (index: number) => void;
    // Load more props
    onLoadMore?: () => void;
    canLoadMore?: boolean;
};

const ModalFeedDetail = ({
    modalObject,
    setModalVisible,
    setIsConditionModal,
    onClickImageAtDetailModal,
    onClickVideoAtDetailModal,
    isIOSiPad,
    isConditionModal,
    isImageModal,
    isVideoModal,
    feedList,
    currentIndex,
    onNavigate,
    onLoadMore,
    canLoadMore,
}: Props) => {
    const [isClosing, setIsClosing] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const prevFeedListLengthRef = useRef(feedList?.length || 0);

    const contents = modalObject?.contents;
    const comments = modalObject?.comments;
    const reactions = modalObject?.reactions;
    const scheduleReport = modalObject?.scheduleReport;
    const receivers = modalObject?.receivers;
    const postType = modalObject?.postType;
    const initialPost = modalObject?.initialPost;

    const hasPrev = feedList && currentIndex !== undefined && currentIndex > 0;
    const hasNext = feedList && currentIndex !== undefined && currentIndex < feedList.length - 1;
    const isAtEnd = feedList && currentIndex !== undefined && currentIndex === feedList.length - 1;

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setModalVisible(false);
        }, 250);
    };

    const handlePrev = () => {
        if (hasPrev && onNavigate) {
            onNavigate(currentIndex! - 1);
        }
    };

    const handleNext = () => {
        if (hasNext && onNavigate) {
            onNavigate(currentIndex! + 1);
        } else if (isAtEnd && canLoadMore && onLoadMore && !isLoadingMore) {
            // 마지막 게시물에서 "다음" → 추가 로드
            setIsLoadingMore(true);
            onLoadMore();
        }
    };

    // feedList 길이가 늘어나면 새로 로드된 데이터 → 자동으로 다음 게시물로 이동
    useEffect(() => {
        if (isLoadingMore && feedList) {
            if (feedList.length > prevFeedListLengthRef.current) {
                setIsLoadingMore(false);
                if (onNavigate && currentIndex !== undefined) {
                    onNavigate(currentIndex + 1);
                }
            }
        }
        prevFeedListLengthRef.current = feedList?.length || 0;
    }, [feedList?.length]);

    // 키보드 네비게이션 (미디어 뷰어가 열려있으면 무시)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (isImageModal || isVideoModal) return;
            if (e.key === 'Escape') {
                handleClose();
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                handlePrev();
            } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                handleNext();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex, feedList, isImageModal, isVideoModal]);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    return (
        <>
            <DrawerOverlay $isClosing={isClosing} onClick={handleClose} />
            <DrawerPanel
                $isClosing={isClosing}
                role="dialog"
                aria-modal="true"
                aria-label="피드 상세 읽기 전용"
            >
                {/* Top bar + Navigation (sticky) */}
                <div className="sticky top-0 z-10 bg-white shadow-sm">
                    {/* 상단 바: 배지 + 타임스탬프 + X */}
                    <ModalFeedDetailHeader modalObject={modalObject} setModalVisible={handleClose} topBarOnly />

                    {/* Navigation bar */}
                    {feedList && feedList.length > 1 && (
                        <div className="flex items-center justify-between px-[24px] py-[10px] border-b border-[#E5E7EB]"
                        >
                            <button
                                onClick={handlePrev}
                                disabled={!hasPrev}
                                className={`flex items-center gap-[6px] px-[12px] py-[6px] rounded-[8px] transition-colors cursor-pointer
                                    ${hasPrev ? 'hover:bg-[#F3F4F6] text-[#374151]' : 'text-[#D1D5DB] cursor-not-allowed'}`}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M15 18l-6-6 6-6"/>
                                </svg>
                                <span className="text-[13px] font-medium">이전</span>
                            </button>

                            <span className="text-[13px] text-[#9CA3AF]">
                                {(currentIndex ?? 0) + 1} / {feedList.length}{canLoadMore ? '+' : ''}
                            </span>

                            <button
                                onClick={handleNext}
                                disabled={!hasNext && !canLoadMore}
                                className={`flex items-center gap-[6px] px-[12px] py-[6px] rounded-[8px] transition-colors cursor-pointer
                                    ${(hasNext || canLoadMore) ? 'hover:bg-[#F3F4F6] text-[#374151]' : 'text-[#D1D5DB] cursor-not-allowed'}`}
                            >
                                {isLoadingMore ? (
                                    <span className="text-[13px] font-medium text-[#9CA3AF]">로딩중...</span>
                                ) : (
                                    <>
                                        <span className="text-[13px] font-medium">다음</span>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M9 18l6-6-6-6"/>
                                        </svg>
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>

                {/* Profile + Body */}
                <Col style={{ flex: 1 }}>
                    {/* 프로필 정보 (스크롤 영역) */}
                    <ModalFeedDetailHeader modalObject={modalObject} setModalVisible={handleClose} profileOnly />

                    <ModalFeedDetailBody
                        feed={modalObject}
                        contents={contents}
                        scheduleReport={scheduleReport}
                        receivers={receivers}
                        postType={postType}
                        initialPost={initialPost}
                        setIsConditionModal={setIsConditionModal}
                        onClickImageAtDetailModal={onClickImageAtDetailModal}
                        onClickVideoAtDetailModal={onClickVideoAtDetailModal}
                    />
                    <ModalFeedDetailFooter comments={comments} reactions={reactions} />
                </Col>
            </DrawerPanel>

            {/* iPad일 경우 플로팅 닫기 버튼 */}
            {isIOSiPad && isIOSiPad() && !isConditionModal && !isImageModal && !isVideoModal && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: 60,
                        right: 740,
                        zIndex: 2100,
                        background: 'rgba(0, 0, 0, 0.8)',
                        borderRadius: '8px',
                        width: 100,
                        height: 50,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: 'white',
                        fontSize: '20px',
                        fontWeight: '500',
                    }}
                    onClick={handleClose}
                >
                    닫기
                </div>
            )}
        </>
    );
};

export default ModalFeedDetail;
